import { useEffect } from 'react';

/**
 * 全局触摸阻尼滚动：
 * - 接管 document 的 touch 事件
 * - 快速滑动有惯性
 * - 接近顶部/底部时阻力增大逐渐减速
 */
export function useGlobalDampedScroll() {
  useEffect(() => {
    let startY = 0;
    let lastY = 0;
    let lastTime = 0;
    let velocity = 0;
    let rafId = 0;
    let isTouching = false;
    let targetEl: Element | null = null;

    // 找到实际需要滚动的祖先元素
    const getScrollTarget = (el: Element | null): Element => {
      let node = el;
      while (node && node !== document.documentElement) {
        const style = window.getComputedStyle(node);
        const overflow = style.overflowY;
        if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight) {
          return node;
        }
        node = node.parentElement;
      }
      return document.documentElement;
    };

    const getEdgeDamping = (scrollTop: number, maxScroll: number): number => {
      if (maxScroll <= 0) return 1;
      const threshold = 0.12;
      const topRatio = scrollTop / maxScroll;
      const bottomRatio = 1 - topRatio;
      const nearEdge = Math.min(topRatio, bottomRatio);
      if (nearEdge < threshold) {
        return 0.04 + (nearEdge / threshold) * 0.96;
      }
      return 1.0;
    };

    const onTouchStart = (e: TouchEvent) => {
      isTouching = true;
      cancelAnimationFrame(rafId);
      targetEl = getScrollTarget(e.target as Element);
      startY = e.touches[0].clientY;
      lastY = startY;
      lastTime = Date.now();
      velocity = 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isTouching || !targetEl) return;

      const currentY = e.touches[0].clientY;
      const now = Date.now();
      const dt = Math.max(now - lastTime, 1);
      const dy = lastY - currentY;

      const scrollTop = targetEl === document.documentElement
        ? window.scrollY
        : (targetEl as HTMLElement).scrollTop;
      const scrollHeight = targetEl.scrollHeight;
      const clientHeight = targetEl === document.documentElement
        ? window.innerHeight
        : (targetEl as HTMLElement).clientHeight;
      const maxScroll = scrollHeight - clientHeight;

      const damping = getEdgeDamping(scrollTop, maxScroll);

      // 只在有实际滚动空间时阻止默认行为
      if (maxScroll > 0) {
        e.preventDefault();
        if (targetEl === document.documentElement) {
          window.scrollBy(0, dy * damping);
        } else {
          (targetEl as HTMLElement).scrollTop += dy * damping;
        }
      }

      velocity = (dy / dt) * damping;
      lastY = currentY;
      lastTime = now;
    };

    const onTouchEnd = () => {
      isTouching = false;
      if (!targetEl) return;
      const captured = targetEl;

      const inertia = () => {
        if (Math.abs(velocity) < 0.04) return;

        const scrollTop = captured === document.documentElement
          ? window.scrollY
          : (captured as HTMLElement).scrollTop;
        const scrollHeight = captured.scrollHeight;
        const clientHeight = captured === document.documentElement
          ? window.innerHeight
          : (captured as HTMLElement).clientHeight;
        const maxScroll = scrollHeight - clientHeight;

        const damping = getEdgeDamping(scrollTop, maxScroll);
        const friction = 0.90 * (0.5 + damping * 0.5);

        if (captured === document.documentElement) {
          window.scrollBy(0, velocity * 16);
        } else {
          (captured as HTMLElement).scrollTop += velocity * 16;
        }

        velocity *= friction;
        rafId = requestAnimationFrame(inertia);
      };

      rafId = requestAnimationFrame(inertia);
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, []);
}
